import { PATCodeType } from "@via/utils/constants.tsx";
import { MCP_CONNECTOR_NAME } from "@via/pages/account/profile/connect-ai/connect-clients";

export interface CodeGeneratorParams {
  token: string;
  hostname: string;
  mcpUrl: string;
  hostnameHeader: string;
}

export const generatePythonSDKCode = (params: CodeGeneratorParams): string => {
  const { token, hostname } = params;
  return `from viafoundry import ViaFoundryClient

client = ViaFoundryClient()
client.configure_auth_token(
    hostname="${hostname}",
    token="${token}"
)

# Now use the client normally
processes = client.process.list_processes()`;
};

export const generateRSDKCode = (params: CodeGeneratorParams): string => {
  const { token, hostname } = params;
  return `library(viafoundry)

# Authenticate with token
authenticate_token(
    hostname = "${hostname}",
    token = "${token}"
)

# Now use the SDK functions
processes <- listProcesses()`;
};

export const generateCLICode = (params: CodeGeneratorParams): string => {
  const { token, hostname } = params;
  return `# Configure authentication
foundry configure --hostname ${hostname} --token ${token}

# Test connection
foundry discover`;
};

export const generateCursorCode = (params: CodeGeneratorParams): string => {
  const { token, mcpUrl, hostnameHeader } = params;
  return `// Add to ~/.cursor/mcp.json

{
  "mcpServers": {
    "fconnect": {
      "url": "${mcpUrl}",
      "headers": {
        ${hostnameHeader}"X-Foundry-Connect-Token": "${token}"
      }
    }
  }
}

// After saving, restart Cursor to connect`;
};

export const generateVSCodeCode = (params: CodeGeneratorParams): string => {
  const { token, mcpUrl, hostnameHeader } = params;
  return `// Cmd+Shift+P > "MCP: Add Server..." > HTTP > Enter URL below > Name it "fconnect"
// Then add the headers to .vscode/mcp.json:

{
  "servers": {
    "fconnect": {
      "url": "${mcpUrl}",
      "headers": {
        ${hostnameHeader}"X-Foundry-Connect-Token": "${token}"
      }
    }
  }
}`;
};

// Copilot rule: a server the plugin contributes shadows a user added server
// of the same name. A user added "foundry" would not show up in
// copilot mcp list at all once the published plugin already contributes that
// name, so this snippet registers under "fconnect" instead, on purpose, so
// the credential route stays visible. Do not align this with the Codex name
// below. See spec section 9.
export const generateCopilotCode = (params: CodeGeneratorParams): string => {
  const { mcpUrl, hostnameHeader } = params;
  // Copilot expands ${VAR} inside headers at connect time, so the credential
  // stays in the environment and never lands in mcp-config.json.
  return `// export FOUNDRY_CONNECT_TOKEN=<the value above>
// Then add to ~/.copilot/mcp-config.json:

{
  "mcpServers": {
    "fconnect": {
      "type": "http",
      "url": "${mcpUrl}",
      "headers": {
        ${hostnameHeader}"X-Foundry-Connect-Token": "\${FOUNDRY_CONNECT_TOKEN}"
      }
    }
  }
}`;
};

// Codex rule: a manual entry wins, overriding the plugin's entry of the same
// name. So this snippet keeps "foundry", the same name the connect page's
// Codex command uses, rather than picking a different name the way the
// Copilot snippet above does. See spec section 9.
export const generateCodexCode = (params: CodeGeneratorParams): string => {
  const { mcpUrl } = params;
  // bearer_token_env_var keeps the credential out of the file entirely. The
  // server accepts a via_mcp_ credential in the Authorization position under the
  // same validation as the custom header.
  return `# export FOUNDRY_CONNECT_TOKEN=<the value above>
# Then add to ~/.codex/config.toml:

[mcp_servers.${MCP_CONNECTOR_NAME}]
url = "${mcpUrl}"
bearer_token_env_var = "FOUNDRY_CONNECT_TOKEN"`;
};

export type McpIdeType = "cursor" | "vscode" | "copilot" | "codex";

// A map rather than a ternary, so adding a target is one entry and TypeScript
// fails the build if a member of McpIdeType has no generator.
const MCP_GENERATORS: Record<
  McpIdeType,
  (params: CodeGeneratorParams) => string
> = {
  cursor: generateCursorCode,
  vscode: generateVSCodeCode,
  copilot: generateCopilotCode,
  codex: generateCodexCode,
};

export const getCodeForType = (
  type: PATCodeType,
  params: CodeGeneratorParams,
  mcpIde?: McpIdeType
): string => {
  switch (type) {
    case PATCodeType.PythonSdk:
      return generatePythonSDKCode(params);
    case PATCodeType.RSdk:
      return generateRSDKCode(params);
    case PATCodeType.Cli:
      return generateCLICode(params);
    case PATCodeType.Mcp:
      // Cursor is PAT_MCP_IDE_OPTIONS's first entry and the modal initializes
      // its picker to that option, so an omitted argument here matches what
      // the picker already shows.
      return MCP_GENERATORS[mcpIde ?? "cursor"](params);
  }
};

export const buildHostnameHeader = (
  isLocalhostEnv: boolean,
  hostname: string
): string => {
  return isLocalhostEnv
    ? `"X-Foundry-Connect-Hostname": "${hostname}",\n        `
    : "";
};
